import React from 'react';
import api from 'api';
import { Form, Input, Modal } from 'antd';
import UploadingComponent from 'components/UploadingComponent/UploadingComponent';
import { IDocument, IFolder } from 'interfaces';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { setAllTagsDocuments, setRoomAllChats } from 'store/slices/chatsSlice';
import { setDocuments, setEntityOfContextMenu, setSelectedDocument, setNestedDocumentsAndFolders } from 'store/slices/dataDocumentsSlice';
import { handleInputChangeWithAltFilter, handlePasteWithAltFilter } from 'helpers/filterAltCodes';
import { normalizeWhitespace } from 'helpers/stringCaseManager';
import { setFoldersForPermissions } from 'store/slices/dataPermissionsSlice';
import { getTheme } from 'helpers/getTheme';
import classes from './RenameModal.module.scss';

type Props = {
  open: boolean;
  entity: IDocument | IFolder;
  type: 'Document' | 'Folder';
  onCancel: () => void;
};

export default function RenameModal({ open, onCancel, entity, type }: Props) {
  const [value, setValue] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);

  const { documents, folders, selectedDocument, nestedDocuments, nestedFolders } = useAppSelector((state) => state.documents);
  const { roomAllChats, allTagsDocuments } = useAppSelector((state) => state.chat);
  const { dataRoom } = useAppSelector((store) => store.dataRoom);
  const { foldersForPermissions } = useAppSelector((store) => store.permissions);
  const { messageApi } = useAppSelector((store) => store.windowState);

  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const { t } = useTranslation();

  const entityName = type === 'Document' 
      ? entity?.name?.split('.').slice(0, -1).join('.')
      : entity?.name;

  React.useEffect(() => {
    form.setFieldsValue({
      name: entityName,
    });
    setValue(entityName);
  }, []);

  const onFinish = async (values: any) => {
    try {
      setIsLoading(true);
      if (type === 'Folder') {
        const normalizedName = normalizeWhitespace(values.name);
        await api.renameFolder(entity.id!, { name: normalizedName });
        dispatch(
          setDocuments({
            documents,
            folders: folders.map((folder) => (folder.id === entity.id ? { ...folder, name: normalizedName } : folder)),
          })
        );
        dispatch(setNestedDocumentsAndFolders({
          documents: nestedDocuments,
          folders: nestedFolders.map((folder) => (folder.id === entity.id ? { ...folder, name: normalizedName } : folder)),
        }));
        dispatch(setFoldersForPermissions(
          foldersForPermissions.map((folder) => (folder.id === entity.id ? { ...folder, name: normalizedName } : folder))
        ));
      } else {
        const normalizedName = normalizeWhitespace(values.name);
        const fullFileName = `${normalizedName}.${entity.extension}`;
        const response = await api.validateDocuments({ document_name: fullFileName, room_id: dataRoom?.id! });

        if (response.data.documents.length > 0) {
          form.setFields([{ name: 'name', errors: [t('Documents.error.dublicate')] }]);
          return;
        }

        await api.renameDocument({ name: fullFileName, id: entity.id! });
        dispatch(
          setDocuments({
            documents: documents.map((file) => (file.id === entity.id ? { ...file, name: fullFileName } : file)),
            folders: folders,
          })
        );
        dispatch(setNestedDocumentsAndFolders({
          documents: nestedDocuments.map((file) => (file.id === entity.id ? { ...file, name: fullFileName } : file)),
          folders: nestedFolders,
        }));
        selectedDocument && dispatch(setSelectedDocument({...selectedDocument, name: fullFileName}));
        selectedDocument && dispatch(setEntityOfContextMenu({...selectedDocument, name: fullFileName}));

        const chatsReplacedBasedOn = roomAllChats.map((chat) => chat.entity_id === selectedDocument?.id
          ? { ...chat, entity_name: fullFileName, title: fullFileName } : chat
        );
        dispatch(setRoomAllChats(chatsReplacedBasedOn));
        dispatch(setAllTagsDocuments(allTagsDocuments.map(doc => doc.id === selectedDocument?.id
          ? { ...doc, name: fullFileName } : doc
        )));
      }
      onCancel();
    } catch (err) {
      messageApi?.error(t('Documents.error.failedRename'));
    } finally {
      setIsLoading(false);
    }
  };

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleInputChangeWithAltFilter(event, (filteredValue) => {
      setValue(filteredValue);
      form.setFieldValue('name', filteredValue);
      form.setFields([{ name: 'name', errors: [] }]);
    });
  };

  const onPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    handlePasteWithAltFilter(event, value, (filteredValue) => {
      setValue(filteredValue);
      form.setFieldValue('name', filteredValue);
      form.setFields([{ name: 'name', errors: [] }]);
    });
  };

  return (
    <Modal
      open={open}
      className={getTheme()}
      onCancel={onCancel}
      cancelText={t('main.cancelButton')}
      title={`${t('Documents.contextMenu.rename')} ${
        entity?.type === 'folder' ? t('Documents.modals.delete.folder') : t('Documents.modals.delete.file')
      }`}
      okButtonProps={{ disabled: form.getFieldsError()[0]?.errors.length > 0 || value === entityName || !value || !value.trim(), loading: isLoading }}
      onOk={form.submit}
      styles={{
        body: {
          padding: '40px 20px 20px 20px',
        },
      }}
    >
      <Form form={form} onFinish={onFinish} >
        <Form.Item className={classes.item} name='name' >
          <Input
            count={{ max: 150, show: true }}
            maxLength={150}
            value={value}
            onChange={onChange}
            onPaste={onPaste}
          />
        </Form.Item>
      </Form>

      {isLoading && <UploadingComponent />} 
    </Modal>
  );
}
